import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * EditableImage — wraps an <img> with an admin-only upload overlay.
 * - pageKey: which page's CMS bucket to save under (e.g. "homepage")
 * - slot: unique key for this specific image (e.g. "hero-bg")
 * - defaultSrc: original/default image URL
 * - overrideSrc: optional already-saved override URL
 * - onSaved(url): callback after successful upload
 *
 * For admins: shows a hover overlay with "Skift billede" button.
 * For everyone else: renders as a plain <img>.
 */
export const EditableImage = ({
  pageKey,
  slot,
  defaultSrc,
  overrideSrc,
  onSaved,
  className = "",
  style = {},
  alt = "",
  testId,
  ...rest
}) => {
  const { isModerator, getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlField, setUrlField] = useState("");

  const currentSrc = overrideSrc || defaultSrc;

  const persistImageUrl = async (url) => {
    try {
      const token = getToken();
      // Read existing fields, set image_<slot>, write back
      const existing = await axios.get(`${API}/settings/page/${pageKey}`);
      const fields = { ...(existing.data.fields || {}), [`image_${slot}`]: url };
      await axios.put(`${API}/settings/page/${pageKey}`, { fields }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Billede gemt!");
      onSaved && onSaved(url);
      setOpen(false);
    } catch {
      toast.error("Kunne ikke gemme");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = getToken();
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/settings/page/${pageKey}/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const fullUrl = res.data.url.startsWith("http") ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      await persistImageUrl(fullUrl);
    } catch {
      toast.error("Kunne ikke uploade");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Non-admin: plain image
  if (!isModerator) {
    return <img src={currentSrc} alt={alt} className={className} style={style} data-testid={testId} {...rest} />;
  }

  // Admin: editable image with hover overlay
  return (
    <>
      <div
        className={`relative inline-block group cursor-pointer ${className}`}
        style={style}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`editable-image-${slot}`}
      >
        <img src={currentSrc} alt={alt} className="w-full h-full object-cover" {...rest} />
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
          <div className="bg-blue-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm font-bold">
            <Upload size={14} /> Skift billede
          </div>
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid={`editable-image-modal-${slot}`}
        >
          <div className="bg-neutral-950 border border-blue-500/30 rounded-xl p-5 max-w-md w-full space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-['Oswald'] font-bold uppercase">Skift billede</h3>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>

            <div className="rounded-lg overflow-hidden border border-white/10 max-h-48">
              <img src={currentSrc} alt="" className="w-full h-auto" />
            </div>

            <label className="block">
              <span className="block text-xs text-gray-400 mb-1">Upload nyt billede fra din telefon/computer</span>
              <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg cursor-pointer font-bold text-sm w-full justify-center">
                <Upload size={14} />
                {uploading ? "Uploader..." : "Vælg billede"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="hidden"
                  data-testid={`editable-upload-${slot}`}
                />
              </label>
            </label>

            <div className="border-t border-white/10 pt-3">
              <span className="block text-xs text-gray-400 mb-1">…eller indsæt en URL:</span>
              <div className="flex gap-2">
                <input
                  value={urlField}
                  onChange={(e) => setUrlField(e.target.value)}
                  placeholder="https://..."
                  className="mc-input flex-1 text-sm"
                />
                <button
                  onClick={() => urlField && persistImageUrl(urlField)}
                  disabled={!urlField}
                  className="chrome-btn px-3 py-2 rounded text-sm disabled:opacity-40"
                >Gem</button>
              </div>
            </div>

            <button onClick={() => setOpen(false)} className="ghost-blue-btn w-full py-2 rounded-lg text-sm flex items-center justify-center gap-2"><X size={14} /> Annuller</button>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * EditableBackground — wrap a div with an editable background-image
 */
export const EditableBackground = ({
  pageKey,
  slot,
  defaultSrc,
  overrideSrc,
  onSaved,
  className = "",
  style = {},
  children,
  testId,
  ...rest
}) => {
  const { isModerator, getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlField, setUrlField] = useState("");
  const currentSrc = overrideSrc || defaultSrc;

  const bgStyle = {
    ...style,
    backgroundImage: `url('${currentSrc}')`,
    backgroundSize: style.backgroundSize || "cover",
    backgroundPosition: style.backgroundPosition || "center",
  };

  const persistImageUrl = async (url) => {
    try {
      const token = getToken();
      const existing = await axios.get(`${API}/settings/page/${pageKey}`);
      const fields = { ...(existing.data.fields || {}), [`image_${slot}`]: url };
      await axios.put(`${API}/settings/page/${pageKey}`, { fields }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Baggrund opdateret!");
      onSaved && onSaved(url);
      setOpen(false);
    } catch { toast.error("Kunne ikke gemme"); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = getToken();
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/settings/page/${pageKey}/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const fullUrl = res.data.url.startsWith("http") ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      await persistImageUrl(fullUrl);
    } catch { toast.error("Kunne ikke uploade"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className={`relative ${className}`} style={bgStyle} data-testid={testId} {...rest}>
      {isModerator && (
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className="absolute top-3 left-3 z-20 bg-blue-600 hover:bg-blue-500 text-white px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 text-xs font-bold shadow-lg"
          data-testid={`editable-bg-btn-${slot}`}
        >
          <Upload size={12} /> Skift baggrund
        </button>
      )}
      {children}

      {open && (
        <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }} data-testid={`editable-bg-modal-${slot}`}>
          <div className="bg-neutral-950 border border-blue-500/30 rounded-xl p-5 max-w-md w-full space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-['Oswald'] font-bold uppercase">Skift baggrundsbillede</h3>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>
            <div className="rounded-lg overflow-hidden border border-white/10 max-h-48">
              <img src={currentSrc} alt="" className="w-full h-auto" />
            </div>
            <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg cursor-pointer font-bold text-sm w-full justify-center">
              <Upload size={14} />
              {uploading ? "Uploader..." : "Vælg billede"}
              <input type="file" accept="image/*" onChange={handleFileUpload} disabled={uploading} className="hidden" data-testid={`editable-bg-upload-${slot}`} />
            </label>
            <div className="border-t border-white/10 pt-3">
              <span className="block text-xs text-gray-400 mb-1">…eller indsæt en URL:</span>
              <div className="flex gap-2">
                <input value={urlField} onChange={(e) => setUrlField(e.target.value)} placeholder="https://..." className="mc-input flex-1 text-sm" />
                <button onClick={() => urlField && persistImageUrl(urlField)} disabled={!urlField} className="chrome-btn px-3 py-2 rounded text-sm disabled:opacity-40">Gem</button>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="ghost-blue-btn w-full py-2 rounded-lg text-sm flex items-center justify-center gap-2"><X size={14} /> Annuller</button>
          </div>
        </div>
      )}
    </div>
  );
};
